import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Homepage from './components/Homepage';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import AddCentro from './components/AddCentro';
import CreateAgreement from './components/CreateAgreement';
import ProofViewer from './components/ProofViewer';

/* ── Protected route wrapper ── */
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <Layout>
        <div className="page-content">
          <div className="loading-screen">
            <div className="loading-spinner" />
            <p>Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

/* ── /login page ── */
function LoginPage() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/app" replace />;
  return (
    <Layout>
      <div className="page-content-narrow">
        <Auth />
      </div>
    </Layout>
  );
}

/* ── /app/* pages ── */
function AppDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <Layout>
      <div className="page-content">
        <Dashboard userId={user.userId} />
        <div className="content-card" style={{ marginTop: 0 }}>
          <h2>Welcome back, {user.userId}</h2>
          <p style={{ color: '#666', lineHeight: 1.7, marginBottom: 24 }}>
            Manage your Centro reserve holdings and settlement agreements on the
            Hyperledger Fabric distributed ledger.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { label: 'Buy Centro',         desc: 'Purchase reserve-backed digital tokens',           path: '/app/buy-centro' },
              { label: 'Create Agreement',   desc: 'Set up a new escrow settlement agreement',         path: '/app/create' },
              { label: 'View Agreements',    desc: 'Browse and manage your existing agreements',       path: '/app/agreements' },
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  background: '#f9f9f9', border: '1px solid #e8e8e8', borderRadius: 6,
                  padding: '20px 18px', textAlign: 'left', cursor: 'pointer',
                  transition: 'border-color 0.15s', fontFamily: 'inherit',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#006CB8'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8e8'}
              >
                <div style={{ fontWeight: 700, color: '#1a2e4a', marginBottom: 6, fontSize: '0.95rem' }}>{item.label}</div>
                <div style={{ fontSize: '0.82rem', color: '#888', lineHeight: 1.5 }}>{item.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function BuyCentroPage() {
  const { user } = useAuth();
  return (
    <Layout>
      <div className="page-content" style={{ maxWidth: 640 }}>
        <AddCentro userId={user.userId} />
      </div>
    </Layout>
  );
}

function CreateAgreementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <Layout>
      <div className="page-content" style={{ maxWidth: 640 }}>
        <CreateAgreement
          userId={user.userId}
          onAgreementCreated={() => navigate('/app/agreements')}
        />
      </div>
    </Layout>
  );
}

function AgreementsPage() {
  const { user } = useAuth();
  return (
    <Layout>
      <div className="page-content">
        <ProofViewer userId={user.userId} />
      </div>
    </Layout>
  );
}

/* ── Root app ── */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/"    element={<Homepage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/app" element={<ProtectedRoute><AppDashboard /></ProtectedRoute>} />
          <Route path="/app/buy-centro" element={<ProtectedRoute><BuyCentroPage /></ProtectedRoute>} />
          <Route path="/app/create" element={<ProtectedRoute><CreateAgreementPage /></ProtectedRoute>} />
          <Route path="/app/agreements" element={<ProtectedRoute><AgreementsPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
